import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useEffect } from "react";
import { Button, Flex, Table, Dropdown } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import DropdownMenu from "./DropdownMenu";
import { useSnackbar } from "../../context/SnackBarContext";

const SkillsTable = () => {
  const { t } = useTranslation();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem("id");
      const res = await axios.get(
        `${API_BASE_URL}/api/skill/user/` + userId,
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token || "",
          },
        }
      );

      if (res.data.responseCode === 429){
        showSnackbar(res.data.message, "error");
        return;
      }
      setData(res.data.skills);
    } catch (error) {
      console.error("Error fetching skills:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);

      const skillToDelete = data.find((skill) => skill._id === id);
      const skillName = skillToDelete?.name || t("skillsTable.defaultSkill");

      const response = await axios.delete(
        `${API_BASE_URL}/api/skill/${id}/`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token || "",
          },
        }
      );

      if (response.data.responseCode === 429){
        showSnackbar(response.data.message, "error");
        return;
      }

      if (response.status === 200) {
        toast.success(t("skillsTable.deleteSuccess"));
      }

      try {
        await axios.post(`${API_BASE_URL}/api/notification`, {
          recipientId: localStorage.getItem("id"),
          senderId: "Skills App",
          title: t("skillsTable.deleteTitleSuccess"),
          content: t("skillsTable.deleteContentSuccess", {
            name: skillName,
          }),
          urgency: "normal",
        }, {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        });
      } catch (notifError) {
        if (notifError.response?.data?.responseCode === 429) {
          showSnackbar(notifError.response.data.message, "warning");
        }
      }

      fetchSkills();
    } catch (error) {
      console.error("Error deleting skill:", error);

      try {
        await axios.post(`${API_BASE_URL}/api/notification`, {
          recipientId: localStorage.getItem("id"),
          senderId: "Skills App",
          title: t("skillsTable.deleteTitleFailed"),
          content: t("skillsTable.deleteContentFailed"),
          urgency: "normal",
        }, {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        });
      } catch (notifError) {
        if (notifError.response?.data?.responseCode === 429) {
          showSnackbar(notifError.response.data.message, "warning");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunSkill = (skill) => {
    navigate(`/run-skills/${skill.key}`);
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const columns = [
    {
      title: t("skillsTable.columns.name"),
      dataIndex: "name",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.objectives"),
      dataIndex: "objectives",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.taskGuidelines"),
      dataIndex: "taskGuidelines",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.sampleInput"),
      dataIndex: "sampleInput",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.requiredOutput"),
      dataIndex: "requiredOutput",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.files"),
      dataIndex: "files",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.otherRules"),
      dataIndex: "otherRules",
      width: "12.5%",
      ellipsis: true,
    },
    {
      title: t("skillsTable.columns.model"),
      dataIndex: "model",
      width: "12.5%",
      ellipsis: true,
    },
    {
      key: "action",
      title: t("skillsTable.columns.action"),
      width: "10%",
      render: (_, skill) => (
        <Dropdown
          overlay={
            <DropdownMenu
              onDelete={handleDelete}
              onEdit={() => navigate(`/edit-skills/${skill.key}`)}
              onRunSkill={() => handleRunSkill(skill)}
              skill={skill}
            />
          }
          trigger={["click"]}
        >
          <EllipsisOutlined style={{ fontSize: "20px" }} />
        </Dropdown>
      ),
    },
  ];

  const dataSource = data.map((item) => ({
    key: item._id,
    name: item.name,
    objectives: item.objectives,
    taskGuidelines: item.taskGuidelines,
    sampleInput: item.sampleInput,
    requiredOutput: item.requiredOutput,
    files: item.files.map((file) => file.fileName).join(", "),
    otherRules: item.otherRules,
    model: item.model,
  }));

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <Flex gap="middle" vertical>
      <Flex align="center" gap="middle">
        {hasSelected
          ? t("skillsTable.selected", { count: selectedRowKeys.length })
          : null}
      </Flex>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={loading ? [] : dataSource}
        loading={loading}
        locale={{
          emptyText: loading
            ? t("skillsTable.loading")
            : t("skillsTable.noData"),
        }}
      />
    </Flex>
  );
};

export default SkillsTable;
